import { format } from "date-fns";
import { motion } from "framer-motion";
import { Building2, Contact2, Phone, Search, SearchX } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { DatePicker } from "../../../components/ui/date-picker";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useToast } from "../../../hooks/use-toast";
import { useIndividualSearch } from "../hooks/use-offer-tracking";
import { CadastroResultCard } from "./cadastro-result-card";

interface SearchViewProps {
  dbName: string;
  ops: string;
}

const getDefaultDataInicio = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  d.setHours(0, 0, 0, 0);
  return d;
};

export function SearchView({ dbName, ops }: SearchViewProps) {
  const { toast } = useToast();
  // Data exclusiva da aba individual: ofertas exibidas a partir dela
  const [dataInicio, setDataInicio] = useState<Date | undefined>(getDefaultDataInicio());
  const dataInicioStr = dataInicio ? format(dataInicio, "yyyy-MM-dd") : undefined;

  const {
    searchForm,
    fieldErrors,
    result,
    isSearching,
    handleFieldChange,
    handleSearch,
  } = useIndividualSearch({ dbName, ops, dataInicio: dataInicioStr });

  const onSearchClick = () => {
    const res = handleSearch();
    if (!res.valid && res.message) {
      toast({
        title: "Erro de validação",
        description: res.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-2">
        <CardContent className="pt-6">
          <h2 className="mb-4 text-lg font-bold">
            Buscar por CPF, CNPJ ou Telefone
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="telefone"
                  placeholder="+55 (11) 99999-9999"
                  value={searchForm.telefone}
                  onChange={(e) => handleFieldChange("telefone", e.target.value)}
                  className={`pl-9 ${fieldErrors.telefone ? "border-destructive" : ""}`}
                />
              </div>
              {fieldErrors.telefone && (
                <p className="text-sm text-destructive">{fieldErrors.telefone}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cpf">CPF</Label>
              <div className="relative">
                <Contact2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="cpf"
                  placeholder="000.000.000-00"
                  value={searchForm.cpf}
                  onChange={(e) => handleFieldChange("cpf", e.target.value)}
                  className={`pl-9 ${fieldErrors.cpf ? "border-destructive" : ""}`}
                />
              </div>
              {fieldErrors.cpf && (
                <p className="text-sm text-destructive">{fieldErrors.cpf}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="cnpj"
                  placeholder="00.000.000/0000-00"
                  value={searchForm.cnpj}
                  onChange={(e) => handleFieldChange("cnpj", e.target.value)}
                  className={`pl-9 ${fieldErrors.cnpj ? "border-destructive" : ""}`}
                />
              </div>
              {fieldErrors.cnpj && (
                <p className="text-sm text-destructive">{fieldErrors.cnpj}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Ofertas a partir de</Label>
              <DatePicker
                date={dataInicio}
                setDate={setDataInicio}
                placeholder="Data início"
              />
              <p className="text-[11px] text-muted-foreground">
                Mostra todas as ofertas desde esta data (padrão: últimos 7 dias)
              </p>
            </div>

            <div className="space-y-2">
              <Label className="invisible">Buscar</Label>
              <Button
                onClick={onSearchClick}
                disabled={isSearching}
                className="w-full"
              >
                {isSearching ? (
                  <span className="flex items-center gap-2">
                    <motion.div
                      className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Buscando...
                  </span>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Rastrear
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {result && (
        <div className="space-y-4">
          {result.cadastros.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
                <SearchX className="h-8 w-8" />
                <p>Nenhum cadastro encontrado para os dados informados.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {result.dataInicio && (
                <p className="text-sm text-muted-foreground">
                  Exibindo ofertas a partir de{" "}
                  <strong>{result.dataInicio.split("-").reverse().join("/")}</strong>
                </p>
              )}
              {result.hasMultipleCadastros && (
                <div className="rounded-md border border-amber-400/50 bg-amber-50 p-3 text-sm text-amber-800">
                  Este cliente possui <strong>{result.cadastros.length} cadastros</strong>.
                  Confira para qual telefone/documento a oferta foi de fato enviada.
                </div>
              )}
              {result.cadastros.map((c, i) => (
                <CadastroResultCard key={c.cadastroId || i} cadastro={c} index={i} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
